import { DemoPortfolioSeriesItem } from '../../../../store/services/demoApi';

interface AggregatedPortfolio {
  dataPoints: { timestamp: number; value: number }[];
  napDataPoints: { timestamp: number; value: number }[];
  nettoDataPoints: { timestamp: number; value: number }[];
  nettoNapDataPoints: { timestamp: number; value: number }[];
}

interface AggregateOpts {
  excludeBaseAssets?: string[];
  startTimestamp?: number | null;
  endTimestamp?: number | null;
}

interface AssetCursor {
  baseAsset: string;
  startBalance: number;
  series: { timestamp: number; value: number }[];
  napSeries: { timestamp: number; value: number }[];
  cursor: number;
  napCursor: number;
  hasStarted: boolean;
  /** Scale factors for rebasing: when a user-set startTimestamp falls inside
   *  the asset's history, each series is independently scaled so its value at
   *  the window's left edge equals `startBalance`. Reusing the trading scale
   *  for the NAP series (or vice-versa) makes the two lines diverge at the
   *  window start, which is exactly the bug we're avoiding. */
  scale: number;
  napScale: number;
  lastValue: number;
  lastNap: number;
}

const findValueAtOrBefore = (
  series: { timestamp: number; value: number }[],
  ts: number,
): { idx: number; value: number } => {
  // Linear scan from end for typical-recent timestamps; series is short (~1700)
  // so the constant overhead beats binary search overhead in practice.
  for (let i = series.length - 1; i >= 0; i--) {
    if (series[i].timestamp <= ts) return { idx: i, value: series[i].value };
  }
  return { idx: -1, value: 0 };
};

/**
 * Aggregate per-asset demo series into a single portfolio with the dashboard's
 * Settings applied entirely client-side:
 *
 *  - excludeBaseAssets drops assets from the sum entirely.
 *  - startTimestamp / endTimestamp clip the visible window.
 *  - When `startTimestamp` is set AND it falls inside an asset's history, the
 *    asset is rebased: its value at startTimestamp is treated as `startBalance`,
 *    every subsequent point scaled proportionally. This preserves the bot's
 *    relative returns ("what if you joined at this date") instead of carrying
 *    the cumulative since-inception magnitude into the window.
 *  - When an asset's own history starts after `startTimestamp`, it joins the
 *    portfolio at its native start with its original `startBalance` baseline.
 *
 * Returns `dataPoints` (trading), `napDataPoints` (buy-and-hold), and
 * `netto*` versions (each minus the cumulative starting capital of assets
 * active by each timestamp).
 */
export const aggregateDemoPortfolio = (
  rawSeries: DemoPortfolioSeriesItem[],
  opts: AggregateOpts = {},
): AggregatedPortfolio => {
  const { excludeBaseAssets, startTimestamp, endTimestamp } = opts;
  const excluded = new Set(excludeBaseAssets ?? []);
  const active = rawSeries.filter((s) => !excluded.has(s.baseAsset));
  if (!active.length) {
    return {
      dataPoints: [],
      napDataPoints: [],
      nettoDataPoints: [],
      nettoNapDataPoints: [],
    };
  }

  const cursors: AssetCursor[] = active.map((s) => {
    const series = [...s.dataPoints].sort((a, b) => a.timestamp - b.timestamp);
    const napSeries = [...s.napDataPoints].sort(
      (a, b) => a.timestamp - b.timestamp,
    );

    // Rebase each series independently so both land at exactly startBalance
    // at the window's left edge.
    let scale = 1;
    let napScale = 1;
    if (startTimestamp != null && series.length) {
      const firstTs = series[0].timestamp;
      if (startTimestamp > firstTs) {
        const valueAtStart = findValueAtOrBefore(series, startTimestamp).value;
        if (valueAtStart > 0) scale = s.startBalanceInUsdt / valueAtStart;
        const napAtStart = findValueAtOrBefore(napSeries, startTimestamp).value;
        if (napAtStart > 0) napScale = s.startBalanceInUsdt / napAtStart;
      }
    }

    return {
      baseAsset: s.baseAsset,
      startBalance: s.startBalanceInUsdt,
      series,
      napSeries,
      cursor: 0,
      napCursor: 0,
      hasStarted: false,
      scale,
      napScale,
      lastValue: 0,
      lastNap: 0,
    };
  });

  // Union of all timestamps across active assets, clipped to the visible
  // window. The aggregation only emits points within [startTimestamp,
  // endTimestamp] but the cursor advances through the full history so each
  // asset's `hasStarted` and `lastValue` reflect reality at every emitted ts.
  const allTimestamps = new Set<number>();
  for (const c of cursors)
    for (const p of c.series) allTimestamps.add(p.timestamp);
  const sortedTs = [...allTimestamps].sort((a, b) => a - b);

  const dataPoints: { timestamp: number; value: number }[] = [];
  const napDataPoints: { timestamp: number; value: number }[] = [];
  const nettoDataPoints: { timestamp: number; value: number }[] = [];
  const nettoNapDataPoints: { timestamp: number; value: number }[] = [];

  for (const ts of sortedTs) {
    // Advance every cursor up to ts.
    for (const c of cursors) {
      while (c.cursor < c.series.length && c.series[c.cursor].timestamp <= ts) {
        c.lastValue = c.series[c.cursor].value;
        c.hasStarted = true;
        c.cursor++;
      }
      while (
        c.napCursor < c.napSeries.length &&
        c.napSeries[c.napCursor].timestamp <= ts
      ) {
        c.lastNap = c.napSeries[c.napCursor].value;
        c.napCursor++;
      }
    }

    if (startTimestamp != null && ts < startTimestamp) continue;
    if (endTimestamp != null && ts > endTimestamp) continue;

    let total = 0;
    let napTotal = 0;
    let invested = 0;
    for (const c of cursors) {
      if (!c.hasStarted) continue;
      total += c.lastValue * c.scale;
      napTotal += c.lastNap * c.napScale;
      invested += c.startBalance;
    }
    dataPoints.push({ timestamp: ts, value: total });
    napDataPoints.push({ timestamp: ts, value: napTotal });
    nettoDataPoints.push({ timestamp: ts, value: total - invested });
    nettoNapDataPoints.push({ timestamp: ts, value: napTotal - invested });
  }

  return { dataPoints, napDataPoints, nettoDataPoints, nettoNapDataPoints };
};
